<template>
    <lb-dialog 
        v-model="internalDialog" 
        :loading="loading" 
        heading="Workflow Configuration Upload" 
        width="60%"
        data-testid="workflow-bulk-upload__dialog"
    >
        <template v-slot:body>
            <div data-testid="workflow-bulk-upload__body">
                <div v-if="fileData.length === 0">
                    <div class="mb-2 d-flex flex-column align-center justify-center gap-3">
                        <div class="d-flex justify-start" style="width: 100%">
                            <v-btn 
                                color="primary" 
                                @click="handleDownloadTemplate()"
                                data-testid="workflow-bulk-upload__download-template-btn"
                            >
                                Download Template
                            </v-btn>
                        </div>
                        <DragDropFileWithIcon
                            :files="file ? [file] : []"
                            :loading="loading"
                            :multiplefiles="false"
                            accept=".xlsx,.xls,.xlsm,.xlsb"
                            @update:files="handleFileUpdate"
                        />
                    </div>
                    <div class="grey--text pa-6 text-center title" data-testid="workflow-bulk-upload__empty-state"> 
                        Download the template, update the details and upload the file.
                    </div>
                </div>

                <div v-else class="d-flex flex-column gap-3" data-testid="workflow-bulk-upload__table-container">
                    <div class="d-flex justify-space-between gap-2">
                        <v-btn 
                            color="primary" 
                            @click="handleDownloadTemplate()"
                        >
                            Download Template
                        </v-btn>
                        <v-btn class="secondaryBtn" outlined @click="reUploadFile">
                            <v-icon left>mdi-upload</v-icon>
                            <span>Re-upload</span>
                        </v-btn>
                    </div>

                    <v-simple-table data-testid="workflow-bulk-upload__table">
                        <thead class="tableHeaderBg workflow-bulk-upload__table-header">
                            <tr>
                                <th scope="" v-for="(header, index) in displayColumns" :key="index" class="text-start">
                                    {{ header }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr 
                                v-for="(row, index) in fileData" 
                                :key="index" 
                                :class="{ 'error-row': !row.isValid }"
                                :data-testid="`workflow-bulk-upload__row-${index}`"
                            >
                                <td :data-testid="`workflow-bulk-upload__row-${index}-rucode`">
                                    <div>{{ row.rucode }}</div>
                                    <div v-if="row.errors.rucode" class="error--text">{{ row.errors.rucode }}</div>
                                </td>
                                <td :data-testid="`workflow-bulk-upload__row-${index}-name`">{{ row.name }}</td>
                                <td 
                                    v-for="field in dynamicFields" 
                                    :key="field"
                                    :data-testid="`workflow-bulk-upload__row-${index}-${field}`"
                                >
                                    <div>{{ capitalizeFirst(row[field]) }}</div>
                                    <div v-if="row.errors[field]" class="error--text">{{ row.errors[field] }}</div>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </div>
            </div>
        </template>
        <template v-slot:actions>
            <div v-if="hasErrors" class="error--text font-weight-bold" data-testid="workflow-bulk-upload__error-msg"> 
                We found few errors. Please rectify the same to proceed with the upload
            </div>
            <v-spacer></v-spacer>
            <v-btn 
                v-if="!hasErrors && fileData.length > 0"
                @click="handleSubmit()" 
                color=""  
                class="primaryBtn"
                :disabled='hasErrors'
                data-testid="workflow-bulk-upload__submit-btn"
            >
                Proceed
            </v-btn>
        </template>
    </lb-dialog>
</template>

<script>
import styledxlsx from 'sheetjs-style';
import { mapActions } from 'vuex';
import DragDropFileWithIcon from "../../../../../views/Inputs/DragDropFileWithIcon.vue";
import { ensureAccessHierarchy } from '../../../utils/helper';

// Constants for Excel styling
const EXCEL_STYLES = {
    PRIMARY_COLOR: "ccc0da",
    HEADING2: "c5d9f1",           // P&L main header color
    HEADING2_LIGHT: "e4f0fc",     // P&L sub-header color (lighter)
    HEADING3: "f0b681",           // BS main header color
    HEADING3_LIGHT: "f8dcb8",     // BS sub-header color (lighter)
    BORDER: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
    }
};

const REQUIRED_FIELDS = [
    "localcontrol_proforma",
    "localmanagement_proforma",
    "regionalcontrol_proforma",
    "regionalmanagement_proforma",
    "localcontrol_forecast",
    "localmanagement_forecast",
    "regionalcontrol_forecast",
    "regionalmanagement_forecast"
];

const SUB_HEADERS = [
    "Local Proforma Control", "Local Proforma Management", "Regional Proforma Control", "Regional Proforma Management",
    "Local Forecast Control", "Local Forecast Management", "Regional Forecast Control", "Regional Forecast Management"
];


export default {
    name: 'WorkflowBulkUpload',
    components: {
        DragDropFileWithIcon
    },
    props: {
        value: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            file: null,
            fileData: [],
            hasErrors: false,
            columns: [],
            displayColumns: [],
            fullRUListFormatted: [],
            dynamicFields: []
        };
    },
    computed: {
        internalDialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        storeHierarchyData() {
            const dynamicState = this.$store.state.m_varianceanalysis || {};
            const staticState = this.$store.state.varianceanalysis || {};
            return dynamicState.actualhierarchydata || staticState.actualhierarchydata || {};
        }
    },
        async created() {
        // Generate flattened headers for the table display
        const plHeaders = SUB_HEADERS.map(h => `P&L - ${h}`);
        const bsHeaders = SUB_HEADERS.map(h => `BS - ${h}`);
        this.columns = [...plHeaders, ...bsHeaders];
        this.displayColumns = ["RU Code", "RU Description", ...this.columns];

        // Dynamic fields for v-for in template (flattened keys)
        this.dynamicFields = [
            ...REQUIRED_FIELDS.map(f => `pl_${f}`),
            ...REQUIRED_FIELDS.map(f => `bs_${f}`)
        ];
    },
    watch: {
        value(val) {
            if (val) {
                this.resetForm();
                this.loadReportingUnits();
            }
        },
        storeHierarchyData(newData) {
            // When hierarchy data arrives in store (from Filter.vue or any other caller),
            // update the local RU list and stop loading
            if (newData && Object.keys(newData).length > 0 && Array.isArray(newData.reportingunitlist)) {
                this.fullRUListFormatted = newData.reportingunitlist.map(item => ({
                    _id: item._id,
                    code: item.code,
                    name: item.name
                }));
                if (this.loading) {
                    this.loading = false;
                }
            }
        }
    },
    beforeDestroy() {
        this.resetForm();
    },
    methods: {
        ...mapActions('varianceanalysis/reportingUnit', ['uploadBulkFileWorkflow']),

        capitalizeFirst(val) {
            if (!val) return '';
            return val.charAt(0).toUpperCase() + val.slice(1);
        },

        resetForm() {
            this.file = null;
            this.fileData = [];
            this.hasErrors = false;
        },

        reUploadFile() {
            this.resetForm();
        },

        handleFileUpdate(files) {
            if (files && files.length > 0) {
                this.file = files[0];
                this.onFileChange();
            } else {
                this.file = null;
            }
        },

        async loadReportingUnits() {
            // If data is already available in the store, use it directly
            const existingData = this.storeHierarchyData;
            if (existingData && Object.keys(existingData).length > 0 && Array.isArray(existingData.reportingunitlist) && existingData.reportingunitlist.length > 0) {
                this.fullRUListFormatted = existingData.reportingunitlist.map(item => ({
                    _id: item._id,
                    code: item.code,
                    name: item.name
                }));
                return;
            }

            // Data not yet available — show loading and wait for it
            try {
                this.loading = true;
                const hierarchyData = await ensureAccessHierarchy(this.$store, this.axios);
                const ruList = hierarchyData.reportingunitlist || [];
                this.fullRUListFormatted = ruList.map(item => ({
                    _id: item._id,
                    code: item.code,
                    name: item.name
                }));
            } catch (error) {
                console.error("[WorkflowBulkUpload]: Failed to fetch RU list for validation", error);
            } finally {
                this.loading = false;
            }
        },

        async handleDownloadTemplate() {
            try {
                const workbook = styledxlsx.utils.book_new();

                // Row 1: Title
                const row1 = [ 
                    { v: "Enable/Disable Workflow", t: "s", s: { font: { bold: true, sz: 14 } } }
                ];
                
                // Row 2: Empty
                const row2 = [];

                // Row 3: Super Headers
                const row3 = [
                    { v: "RU Code", t: "s", s: { font: { bold: true }, alignment: { horizontal: "center", vertical: "center" }, fill: { fgColor: { rgb: EXCEL_STYLES.PRIMARY_COLOR } }, border: EXCEL_STYLES.BORDER } },
                    { v: "PROFIT & LOSS", t: "s", s: { font: { bold: true }, alignment: { horizontal: "center" }, fill: { fgColor: { rgb: EXCEL_STYLES.HEADING2 } }, border: EXCEL_STYLES.BORDER } }, 
                    "", "", "", "", "", "", "", // Spacer for merge P&L
                    { v: "BALANCE SHEET", t: "s", s: { font: { bold: true }, alignment: { horizontal: "center" }, fill: { fgColor: { rgb: EXCEL_STYLES.HEADING3 } }, border: EXCEL_STYLES.BORDER } },
                    "", "", "", "", "", "", ""  // Spacer for merge BS
                ];

                // Row 4: Sub Headers
                const subHeaderStyles = (isPL) => ({
                    font: { bold: true },
                    fill: { fgColor: { rgb: isPL ? EXCEL_STYLES.HEADING2_LIGHT : EXCEL_STYLES.HEADING3_LIGHT } },
                    alignment: { wrapText: true, horizontal: "center", vertical: "center" },
                    border: EXCEL_STYLES.BORDER
                });

                const row4 = [
                    "", // RU Code placeholder
                    ...SUB_HEADERS.map(h => ({ v: h, t: "s", s: subHeaderStyles(true) })),
                    ...SUB_HEADERS.map(h => ({ v: h, t: "s", s: subHeaderStyles(false) }))
                ];

                const wsData = [row1, row2, row3, row4];
                const worksheet = styledxlsx.utils.aoa_to_sheet(wsData);

                // Merges
                worksheet['!merges'] = [
                    { s: { r: 0, c: 0 }, e: { r: 0, c: 16 } }, // Title
                    { s: { r: 2, c: 0 }, e: { r: 3, c: 0 } },  // RU Code rowspan
                    { s: { r: 2, c: 1 }, e: { r: 2, c: 8 } },  // P&L colspan
                    { s: { r: 2, c: 9 }, e: { r: 2, c: 16 } }  // BS colspan
                ];

                worksheet['!cols'] = [
                    { wch: 15 }, 
                    ...new Array(16).fill({ wch: 15 })
                ];

                styledxlsx.utils.book_append_sheet(workbook, worksheet, "TOP SHEET");
                const now = new Date();
                const timestamp = now.toISOString().slice(0, 19).replaceAll(/[-T:]/g, "");
                styledxlsx.writeFile(workbook, `UPDATE_WORKFLOW_STATE_${timestamp}.xlsx`);
            }
            catch (err) {
                this.$store.commit("sbError", err.message || err || "Unknown error!");
                console.error("[WorkflowBulkUpload]: Download template error", err);
            }
        },

        onFileChange() {
            const file = this.file;
            this.fileData = [];
            this.hasErrors = false;

            if (!(file && file.name) || this.loading) return;

            this.loading = true;

            const ext = (file.name || "").split(".").pop();
            const isValidExtension = ["xlsx", "xls", "xlsm", "xlsb"].includes(ext);
            if (!isValidExtension) {
                this.$store.commit("sbError", "Only xlsx,xls,xlsm,xlsb file extension supported");
                this.loading = false;
                return;
            }

            this.$nova.readExcelFile(file, "TOP SHEET").then(dt => {
                if (!dt || dt.length === 0) {
                    throw new Error("The uploaded file is empty.");
                }

                // Robust Header Detection
                // We look for a row that contains enough of our expected columns.
                // "Local Proforma Control" is unique enough.
                let headerRowIndex = -1;
                const KEY_HEADER = SUB_HEADERS[0]; // "Local Proforma Control"

                // Scan first 20 rows
                for (let i = 0; i < Math.min(dt.length, 20); i++) {
                    const row = dt[i];
                    if (Array.isArray(row) && row.includes(KEY_HEADER)) {
                        headerRowIndex = i;
                        break;
                    }
                }

                if (headerRowIndex === -1) {
                     // Fallback/Error if we absolutely can't find the structure
                     throw new Error("Invalid format. Could not locate header row starting with 'Local Proforma Control'.");
                }

                const DATA_START_INDEX = headerRowIndex + 1;

                if (dt.length <= DATA_START_INDEX) {
                    throw new Error("The uploaded file does not contain any data rows after the headers.");
                }

                let parsedData = [];
                const rucodeIdMap = new Map(this.fullRUListFormatted.map(item => [item.code, { _id: item._id, name: item.name }]));
                
                let foundError = false;

                for (let i = DATA_START_INDEX; i < dt.length; i++) {
                    const el = dt[i];
                    
                    if (!el || el.length === 0) continue;

                    let rucode = (el[0] || "").toString().trim();
                    if (!rucode) continue;

                    // Map columns based on relative position to header
                    // We expect: [RU Code, PL1, PL2... PL8, BS1, BS2... BS8]
                    // If the user shifted columns, this might break, but we assume column order is preserved relative to headers.
                    
                    let rowdata = {
                        rucode,
                        _id: null,
                        name: "",
                        // PL Fields (Cols 1-8)
                        pl_localcontrol_proforma: el[1],
                        pl_localmanagement_proforma: el[2],
                        pl_regionalcontrol_proforma: el[3],
                        pl_regionalmanagement_proforma: el[4],
                        pl_localcontrol_forecast: el[5],
                        pl_localmanagement_forecast: el[6],
                        pl_regionalcontrol_forecast: el[7],
                        pl_regionalmanagement_forecast: el[8],
                        // BS Fields (Cols 9-16)
                        bs_localcontrol_proforma: el[9],
                        bs_localmanagement_proforma: el[10],
                        bs_regionalcontrol_proforma: el[11],
                        bs_regionalmanagement_proforma: el[12],
                        bs_localcontrol_forecast: el[13],
                        bs_localmanagement_forecast: el[14],
                        bs_regionalcontrol_forecast: el[15],
                        bs_regionalmanagement_forecast: el[16],
                    };

                    let errors = {};
                    const allKeys = [
                        ...REQUIRED_FIELDS.map(f => `pl_${f}`),
                        ...REQUIRED_FIELDS.map(f => `bs_${f}`)
                    ];

                    allKeys.forEach(field => {
                        let val = (rowdata[field] || "").toString().trim();
                        if (val === "") {
                            // If user leaves it blank in upload, maybe they mean 'keep existing'? 
                            // Requirements say Enable/Disable is required.
                             errors[field] = "Required";
                             foundError = true;
                        } else {
                            // Normalize
                            if (val.toLowerCase() === 'yes') val = 'enable';
                            if (val.toLowerCase() === 'no') val = 'disable';
                            
                            if (!["enable", "disable"].includes(val.toLowerCase())) {
                                errors[field] = "Must be Yes/No";
                                foundError = true;
                            } else {
                                rowdata[field] = val.toLowerCase();
                            }
                        }
                    });

                    if (!rucodeIdMap.has(rucode)) {
                        errors["rucode"] = "Unknown RU";
                        foundError = true;
                    } else {
                        const matched = rucodeIdMap.get(rucode);
                        rowdata._id = matched._id;
                        rowdata.name = matched.name;
                    }

                    const isValid = Object.keys(errors).length === 0;
                    rowdata.errors = errors;
                    rowdata.isValid = isValid;
                    parsedData.push(rowdata);
                }

                if (parsedData.length === 0) {
                    throw new Error("No valid data rows found.");
                }

                this.fileData = parsedData;
                this.hasErrors = foundError;
            }).catch(err => {
                this.$store.commit("sbError", err.message || err || "Unknown error!");
                this.file = null;
            }).finally(() => {
                this.loading = false;
            });
        },

        async handleSubmit() {
            // Validation before submit
            if (this.hasErrors || this.fileData.length === 0) {
                return;
            }

            let workFlowBulkData = [];

            this.fileData.forEach(row => {
                const transform = (val) => {
                     return (val === 'enable') ? true : (val === 'disable') ? false : val;
                };

                let entry = {
                    _id: row._id,
                    reporting_unit: row._id,
                    rucode: row.rucode
                };

                REQUIRED_FIELDS.forEach(f => {
                    // PL (Standard keys)
                    entry[f] = transform(row[`pl_${f}`]);
                    
                    // BS (Suffixed keys)
                    entry[`${f}_bs`] = transform(row[`bs_${f}`]);
                });
                
                workFlowBulkData.push(entry);
            });

            this.loading = true;
            try {
                await this.uploadBulkFileWorkflow({ 
                    payload: workFlowBulkData, 
                    axiosInstance: this.axios 
                });
                
                this.$emit('success');
                this.internalDialog = false;
            } catch (err) {
                // Error handled in store but log here for debugging
                console.error("[WorkflowBulkUpload]: Submit error", err);
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style scoped>
.error-row {
  background-color: #ffeff0; 
}
.workflow-bulk-upload__table-header th {
  white-space: nowrap;
}
</style>